import { useCallback } from 'react'

import {
    getFeatureFlagsAsSemicolonString,
    getDocumentReferrer,
    getCurrentWindowUrl,
    getDisplayedProductsCards,
    toSnakeCase,
} from '@mediaingenuity/totallymoney.cardloanutils'
import {
    Category,
    trackEvent,
} from '@mediaingenuity/totallymoney.trackingevents'

import {
    CreditCardAlternativeProduct,
    ScoredCreditCardProduct,
    ScoredCreditCardProductOfferDetail,
} from '@appTypes/EligibilityApi.interface'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlags from './useFeatureFlags'

type logResultsScreenDisplayedEventProps =
    | {
          category: 'credit_cards'
          displayedProducts?: ScoredCreditCardProduct[]
          highlightProduct: ScoredCreditCardProduct | null
          maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
          numberOfCardsToShow?: number
      }
    | {
          category: 'credit_cards_alt'
          displayedProducts?: CreditCardAlternativeProduct[]
          highlightProduct: null
          maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
          numberOfCardsToShow?: number
      }

type logProductClickEventProps = Pick<
    ScoredCreditCardProduct,
    'name' | 'productCode'
> & {
    apr?: CreditCardAlternativeProduct['apr']
    category: Category
    clickId: string
    guaranteedRate?: ScoredCreditCardProduct['guaranteedRate']
    maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
    productRankOrder: number | null
    productShownOrder: number | null
    provider?: ScoredCreditCardProductOfferDetail['provider']
    representativeAPR?: ScoredCreditCardProductOfferDetail['representativeAPR']
}

// This hook handles logging events 2.0 events to Basin
const useLogEvents2 = () => {
    const { appQueryState } = useAppQueryState()
    const { send } = useBasin()
    const { featureFlags } = useFeatureFlags()

    const logProductClickEvent = useCallback(
        ({
            apr,
            category,
            clickId,
            guaranteedRate,
            maxCreditLimit,
            name,
            productCode,
            productShownOrder,
            productRankOrder,
            provider,
            representativeAPR,
        }: logProductClickEventProps) => {
            const payload = trackEvent('product_click', {
                apr: representativeAPR || apr,
                lender: provider || name,
                product_code: productCode,
                product_name: name,
                product_shown_order: productShownOrder,
                product_rank_order: productRankOrder,
                max_credit_limit: maxCreditLimit,
                guaranteed_credit_limit: guaranteedRate?.guaranteedCreditLimit,

                category,
                click_id: clickId,
                client: 'web',
                customer_id: appQueryState.customerId,
                downsell: appQueryState.referrer === 'loansdownsell',
                gclid: appQueryState.googleClickId,
                origin: getDocumentReferrer(),
                score_id: appQueryState.scoreId!,
                send_id: appQueryState.sendId,
                url: getCurrentWindowUrl(),
                utm_campaign: appQueryState.campaign,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: appQueryState.mediaSource,
                variations: getFeatureFlagsAsSemicolonString(featureFlags),
            })

            send('product_click', payload)
        },
        [send, appQueryState]
    )

    const logResultsScreenDisplayedEvent = useCallback(
        ({
            category,
            displayedProducts,
            maxCreditLimit,
            numberOfCardsToShow,
            highlightProduct,
        }: logResultsScreenDisplayedEventProps) => {
            const payload = trackEvent('results_screen_displayed', {
                url: getCurrentWindowUrl(),
                origin: getDocumentReferrer(),
                variations: getFeatureFlagsAsSemicolonString(featureFlags),
                requested_credit_limit: maxCreditLimit,
                send_id: appQueryState.sendId,
                category,
                client: 'web',
                filter: appQueryState.category
                    ? toSnakeCase(appQueryState.category!)
                    : undefined,
                order_by: appQueryState.sortBy
                    ? toSnakeCase(appQueryState.sortBy!)
                    : undefined,
                displayed_products: getDisplayedProductsCards<
                    ScoredCreditCardProduct | CreditCardAlternativeProduct
                >({
                    products: displayedProducts!,
                    numberOfCardsToShow: numberOfCardsToShow,
                    highlightProduct: highlightProduct,
                    category,
                }),
                customer_id: appQueryState.customerId,
                gclid: appQueryState.googleClickId,
                utm_campaign: appQueryState.campaign,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: appQueryState.mediaSource,
                score_id: appQueryState.scoreId!,
            })

            send('results_screen_displayed', payload)
        },
        [send, appQueryState]
    )

    return {
        logProductClickEvent,
        logResultsScreenDisplayedEvent,
    }
}

export default useLogEvents2
